import React, { useRef, useEffect, useState } from "react";
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered,
  Quote, Link2, Image as ImageIcon, Video, Table as TableIcon, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { parseVideoUrl } from "@/lib/video";

const Btn = ({ onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="flex h-9 w-9 items-center justify-center rounded-sm text-ink/70 transition-colors hover:bg-cream-200 hover:text-gold-600"
  >
    {children}
  </button>
);

export function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ref.current && value !== undefined && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (command, arg = null) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    emit();
  };
  const insertHTML = (html) => {
    ref.current.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Enter the URL (https://…)");
    if (url) cmd("createLink", url);
  };

  const addImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        insertHTML(`<img src="${mediaUrl(data.url)}" alt="" />`);
      } catch {
        toast.error("Image upload failed.");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const addVideo = () => {
    const url = window.prompt("Paste a YouTube or Vimeo link");
    if (!url) return;
    const parsed = parseVideoUrl(url);
    if (!parsed) {
      toast.error("Only YouTube and Vimeo links are supported.");
      return;
    }
    insertHTML(`<div class="gf-embed"><iframe src="${parsed.embed}" loading="lazy" allowfullscreen></iframe></div><p><br/></p>`);
  };

  const addTable = () => {
    insertHTML(
      '<table><thead><tr><th>Heading</th><th>Heading</th></tr></thead>' +
      '<tbody><tr><td>Cell</td><td>Cell</td></tr><tr><td>Cell</td><td>Cell</td></tr></tbody></table><p><br/></p>'
    );
  };

  return (
    <div className="rounded-sm border border-ink/15 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-ink/10 bg-cream-100 px-2 py-1.5">
        <Btn title="Bold" onClick={() => cmd("bold")} testid="rte-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" onClick={() => cmd("italic")} testid="rte-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" onClick={() => cmd("underline")} testid="rte-underline"><Underline className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-ink/10" />
        <Btn title="Heading 2" onClick={() => cmd("formatBlock", "<h2>")} testid="rte-h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Heading 3" onClick={() => cmd("formatBlock", "<h3>")} testid="rte-h3"><Heading3 className="h-4 w-4" /></Btn>
        <Btn title="Quote" onClick={() => cmd("formatBlock", "<blockquote>")} testid="rte-quote"><Quote className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-ink/10" />
        <Btn title="Bullet list" onClick={() => cmd("insertUnorderedList")} testid="rte-ul"><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" onClick={() => cmd("insertOrderedList")} testid="rte-ol"><ListOrdered className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-ink/10" />
        <Btn title="Link" onClick={addLink} testid="rte-link"><Link2 className="h-4 w-4" /></Btn>
        <Btn title="Image" onClick={addImage} testid="rte-image"><ImageIcon className="h-4 w-4" /></Btn>
        <Btn title="Embed video" onClick={addVideo} testid="rte-video"><Video className="h-4 w-4" /></Btn>
        <Btn title="Table" onClick={addTable} testid="rte-table"><TableIcon className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-ink/10" />
        <Btn title="Clear formatting" onClick={() => cmd("removeFormat")} testid="rte-clear"><Eraser className="h-4 w-4" /></Btn>
        {uploading && <span className="ml-2 font-sans text-xs text-steel-500">Uploading…</span>}
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        data-placeholder="Write your article…"
        data-testid="rte-content"
        className="gf-prose min-h-[380px] px-6 py-5 focus:outline-none"
        style={{ fontSize: "1.05rem" }}
        suppressContentEditableWarning
      />
    </div>
  );
}
